import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from '../redux/sagas/axios';
import { CountriesPrefsSelector } from './countries_prefs_selector';

// いくつでも選べるボタンの左に置くチェック。選んだときだけ出す（場所も同時に空く）
const CheckMark = () => (
  <svg
    className="pr-2026__chip-check"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

// 選択肢が多いかたまりは、最初は5行ぶんだけ出して「すべて表示」で開く。
// 200個が一度に並ぶと、その下にある開催日・規模・年齢層まで誰も届かないため
const COLLAPSED_COUNT = 24; // 「選択中」をまとめて出すかどうかの目安（これより多いかたまり）

// 粒の列。5行に収まらないときだけ「すべて表示」を出す。
// 何個で切るかではなく実際の高さで判断するので、画面の広さや言葉の長さが
// 変わっても、いつも「はみ出したときだけボタンが出る」形になる
const CollapsibleChips = ({ children }) => {
  const ref = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const check = () => setIsOverflowing(el.scrollHeight > el.clientHeight + 1);

    check();
    window.addEventListener('resize', check);

    return () => window.removeEventListener('resize', check);
  });

  return (
    <React.Fragment>
      <div ref={ref} className={`pr-2026__chips${isExpanded ? '' : ' is-clamped'}`}>
        {children}
      </div>
      {(isOverflowing || isExpanded) && (
        <button
          type="button"
          className="pr-2026__more"
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? '閉じる' : 'すべて表示'}
        </button>
      )}
    </React.Fragment>
  );
};

// 画面に出す見出しと説明。データが持っている名前（description）は中身と合っていないので
// （「推し活ジャンル」の中身は嵐・鬼滅の刃などの固有名詞、「ジャンル」の中身は飲み会・カラオケ）、
// 表示するときだけ言いかえる。名前そのものを直すにはデータ側の変更が必要。
// ここに無い名前は、データの名前をそのまま出す（開催日・イベントの規模・年齢層）
const GROUP_LABELS = {
  '推し活ジャンル': {
    title: '興味あるもの',
    note: '推しや好きなものを登録すると、おすすめイベントに反映されます',
  },
  'ジャンル': {
    title: '参加してみたいイベントのジャンル',
  },
};

// 画面に出す順番。データの並び順では「興味あるもの」と「ジャンル」のあいだに
// 開催日・規模・年齢層が挟まっていて、似たもの同士が離れてしまうため、
// 表示のときだけ「好きなもの2つ → 条件3つ」の順に並べ替える。
// ここに無い名前は、データの並び順のまま後ろに続く
const GROUP_ORDER = ['推し活ジャンル', 'ジャンル', '開催日', 'イベントの規模', '年齢層'];

// 画面上の3つのまとまり。データには「まとまり」の情報が無いので、ここで決めている。
// 本来はデータ側に持たせるべきもの（エンジニアへの依頼事項）
const SECTIONS = [
  { key: 'interest', members: ['推し活ジャンル'] },
  { key: 'genre', members: ['ジャンル'] },
  { key: 'conditions', title: 'イベントの条件', members: ['開催日', 'イベントの規模', '年齢層'] },
];

const sortGroups = (groups) =>
  [...groups].sort((a, b) => {
    const ia = GROUP_ORDER.indexOf(a.description);
    const ib = GROUP_ORDER.indexOf(b.description);

    return (ia === -1 ? GROUP_ORDER.length : ia) - (ib === -1 ? GROUP_ORDER.length : ib);
  });

// 並び順のとおりに、種類が変わったところで区切ってひとかたまりにする。
// 区切り方は元のまま（preference_type が変わったら次のかたまり）。
// 見出しはデータが持っている名前（description）を使う。
// 以前は種類1のときだけ決め打ちの文字を出していたため、
// 「推し活ジャンル」と「ジャンル」の2か所に同じ見出しが出ていた。
const buildGroups = (preferences) =>
  preferences.reduce((groups, preference) => {
    const last = groups[groups.length - 1];

    if (last && last.preferenceType === preference.preference_type) {
      last.items.push(preference);
    } else {
      groups.push({
        preferenceType: preference.preference_type,
        description: preference.description,
        items: [preference],
      });
    }

    return groups;
  }, []);

const Edit = ({ preferences, initialMyPreferences, isApp, isTutorial, countriesPrefs, currentPrefId, circles = [], currentUserId, registeredCircleIds = [] }) => {
  /* ---------- Preferences ---------- */
  const [isSelectedIds, setIsSelectedIds] = useState(
    initialMyPreferences.map((p) => p.id)
  );
  const [myPreferences, setMyPreferences] = useState(initialMyPreferences);
  /* ---------- OshikatsuCircles ---------- */
  const [keyword, setKeyword] = useState('');
  const [matchedCircles, setMatchedCircles] = useState([]);
  const [similarCircles,  setSimilarCircles]  = useState([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedCircleIds, setSelectedCircleIds] = useState(registeredCircleIds);
  // 一度でも探して出てきたものを覚えておく。別の言葉で探し直したときに、
  // すでに追加したものが画面から消えてしまわないようにするため
  const [foundCircles, setFoundCircles] = useState([]);
  const noResult = hasSearched && !searching && matchedCircles.length === 0 && similarCircles.length === 0;

  // 並べるのは「自分が追加したもの」と「いま探して出てきたもの」だけ。
  // サーバーが既定で渡してくる人気のサークルは、自分で足したものと見分けが
  // つかなくなるので出さない。同じものが二重に並ばないよう id で間引く
  const searchResultIds = new Set(
    [...matchedCircles, ...similarCircles].map((c) => c.id)
  );
  const seenCircleIds = new Set();
  const allCircles = [...circles, ...foundCircles].filter((circle) => {
    if (seenCircleIds.has(circle.id)) return false;
    seenCircleIds.add(circle.id);

    return selectedCircleIds.includes(circle.id) || searchResultIds.has(circle.id);
  });

  const onChangePreferenceIds = useCallback(
    (preference) => {
      let newIsSelectedIds = isSelectedIds;

      if (isSelectedIds.includes(preference.id)) {
        newIsSelectedIds = isSelectedIds.filter((id) => id !== preference.id);
        setIsSelectedIds(newIsSelectedIds);
        setMyPreferences(myPreferences.filter((p) => p.id !== preference.id));
      } else {
        const newMyPreferences =
          preference.preference_type !== 1
            ? myPreferences.filter(
                (p) => p.preference_type !== preference.preference_type
              )
            : myPreferences;

        newIsSelectedIds = newMyPreferences
          .map((p) => p.id)
          .concat(preference.id);

        setIsSelectedIds(newIsSelectedIds);
        setMyPreferences(newMyPreferences.concat(preference));
      }

      try {
        const preferenceIds = JSON.stringify({
          preference_ids: newIsSelectedIds,
        });
        axios.post('/api/preferences_users', preferenceIds);
      } catch (e) {
        alert('通信に失敗しました。');
      }
    },
    [myPreferences, isSelectedIds]
  );

  // かたまりの選択をまとめて外す。送っている中身は1つずつ外したときと同じで、
  // 「いま選んでいるものの全部」を送り直している
  const onClearGroup = useCallback(
    (items) => {
      const groupIds = items.map((p) => p.id);
      const newIsSelectedIds = isSelectedIds.filter((id) => !groupIds.includes(id));

      setIsSelectedIds(newIsSelectedIds);
      setMyPreferences(myPreferences.filter((p) => !groupIds.includes(p.id)));

      try {
        axios.post(
          '/api/preferences_users',
          JSON.stringify({ preference_ids: newIsSelectedIds })
        );
      } catch (e) {
        alert('通信に失敗しました。');
      }
    },
    [myPreferences, isSelectedIds]
  );

  const handleSearch = async () => {
    const kw = keyword.trim();
    if (!kw) return;
      setSearching(true);
    try {
      const { data } = await axios.get(
        '/api/oshikatsu/favorites/search_match_oshikatsu_circle',
        { params: { query: kw } });
        const found = [...(data.exact_matches ?? []), ...(data.similar_matches ?? [])];
        setMatchedCircles(data.exact_matches ?? []);
        setSimilarCircles(data.similar_matches ?? []);
        setFoundCircles((prev) =>
          prev.concat(found.filter((f) => !prev.some((p) => p.id === f.id)))
        );
    } catch (e) {
      alert('検索に失敗しました');
    } finally {
      setSearching(false);
      setHasSearched(true);
    }
  };

  const handleCircleToggle = useCallback(async (circle) => {
    if (!currentUserId) {
      alert('ログインしてください');
      return;
    }
    const already = selectedCircleIds.includes(circle.id);

    setSelectedCircleIds(prev =>
      already ? prev.filter(id => id !== circle.id) : [...prev, circle.id]
    );

    try {
      await axios.post(
        already
          ? '/api/oshikatsu/favorites/unfollow_oshikatsu_circle'
          : '/api/oshikatsu/favorites/follow_oshikatsu_circle',
        { circle_id: circle.id },
      );
    } catch (e) {
      setSelectedCircleIds(prev =>
        already ? [...prev, circle.id] : prev.filter((id) => id !== circle.id)
      );
      alert(already ? '解除に失敗しました' : '登録に失敗しました');
    }
  }, [currentUserId, selectedCircleIds]);

  const groups = sortGroups(buildGroups(preferences));

  // 3つのまとまりに分けて、それぞれ白いカードに入れる。
  // どのまとまりに入るかはデータに情報が無いので、ここで決めている
  const sections = SECTIONS.map((section) => ({
    ...section,
    members: groups.filter((g) => section.members.includes(g.description)),
  })).filter((section) => section.members.length > 0);

  // どのまとまりにも入らないものは、取りこぼさないよう最後に単独で出す
  const placed = SECTIONS.flatMap((s) => s.members);
  groups
    .filter((g) => !placed.includes(g.description))
    .forEach((g) => sections.push({ key: `other-${g.description}`, members: [g] }));

  const renderGroupBody = (group, withSubTitle) => {
    // 種類1だけがいくつでも選べる。ほかは同じかたまりで1つだけ
    const isMultiple = group.preferenceType === 1;
    // 推し活サークルと探す欄は「ジャンル」のかたまりの中に置く
    const isGenreGroup = group.description === 'ジャンル';

    // 選んであるものをまとまりの先頭に出す。選択肢が多いかたまりでは、
    // 選んだものが列の中に散らばって見つけられなくなるため
    const showsSelectedBox = isMultiple && group.items.length > COLLAPSED_COUNT;
    const selectedItems = showsSelectedBox
      ? group.items.filter((item) => isSelectedIds.includes(item.id))
      : [];

    // 上の枠に出したものは、下の列からは外す（同じものが2か所に並ばないように）
    const listItems = showsSelectedBox
      ? group.items.filter((item) => !isSelectedIds.includes(item.id))
      : group.items;

    // 探して追加したサークルも、同じ枠にまとめて出す。
    // 下の列に残るのは「探して出てきたが、まだ追加していないもの」だけになる
    const splitCircles = isGenreGroup && showsSelectedBox;
    const selectedCircles = splitCircles
      ? allCircles.filter((circle) => selectedCircleIds.includes(circle.id))
      : [];
    const listCircles = isGenreGroup
      ? splitCircles
        ? allCircles.filter((circle) => !selectedCircleIds.includes(circle.id))
        : allCircles
      : [];

    return (
      <div className="pr-2026__sub" key={`sub-${group.description}`}>
        {withSubTitle && (
          <div className="pr-2026__sub-head">
            <h3 className="pr-2026__sub-title">{group.description}</h3>
            <span className="pr-2026__tag">{isMultiple ? '複数選択可' : '1つ選択'}</span>
          </div>
        )}

        {selectedItems.length + selectedCircles.length > 0 && (
          <div className="pr-2026__selected">
            <p className="pr-2026__selected-title">
              選択中
              <span className="pr-2026__selected-count">
                {selectedItems.length + selectedCircles.length}
              </span>
            </p>
            <div className="pr-2026__selected-field">
              {selectedItems.map((preference) => (
                <button
                  type="button"
                  key={`sel-${preference.id}`}
                  className="pr-2026__selected-chip"
                  aria-label={`${preference.name} を外す`}
                  onClick={() => onChangePreferenceIds(preference)}
                >
                  {preference.name}
                  <svg
                    className="pr-2026__chip-x"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    aria-hidden="true"
                  >
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              ))}
              {selectedCircles.map((circle) => (
                <button
                  type="button"
                  key={`sel-circle-${circle.id}`}
                  className="pr-2026__selected-chip"
                  aria-label={`${circle.name} を外す`}
                  onClick={() => handleCircleToggle(circle)}
                >
                  {circle.name}
                  <svg
                    className="pr-2026__chip-x"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    aria-hidden="true"
                  >
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              ))}
              <button
                type="button"
                className="pr-2026__selected-clear"
                aria-label="すべて解除"
                onClick={() => onClearGroup(group.items)}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" fill="currentColor" />
                  <path
                    d="M15 9l-6 6M9 9l6 6"
                    stroke="#FFFFFF"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}

        {isGenreGroup && (
          <React.Fragment>
            <div className="pr-2026__search">
              <div className="pr-2026__search-field">
                <svg
                  className="pr-2026__search-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-3.2-3.2" />
                </svg>
                <input
                  type="text"
                  className="pr-2026__search-input"
                  placeholder="追加したいジャンルを入力"
                  aria-label="追加したいジャンルを入力して探す"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                />
              </div>
              <button type="button" className="pr-2026__search-btn" onClick={handleSearch}>
                検索
              </button>
            </div>

            {searching && <p className="pr-2026__search-msg">検索中</p>}
            {noResult && (
              <p className="pr-2026__search-msg">該当する名前が見つかりませんでした</p>
            )}
          </React.Fragment>
        )}

        {/* 探して出てきたものを先に置き、そのすぐ右から既存の選択肢を詰めて並べる */}
        <CollapsibleChips>
          {listCircles.map((circle) => {
            const isSelected = selectedCircleIds.includes(circle.id);

            return (
              <button
                type="button"
                key={`circle-${circle.id}`}
                className={`pr-2026__chip${isSelected ? ' is-selected' : ''}`}
                aria-pressed={isSelected}
                onClick={() => handleCircleToggle(circle)}
              >
                {isSelected && <CheckMark />}
                {circle.name}
              </button>
            );
          })}
          {listItems.map((preference) => {
            const isSelected = isSelectedIds.includes(preference.id);

            return (
              <label
                key={preference.id}
                className={`pr-2026__chip${isSelected ? ' is-selected' : ''}`}
              >
                <input
                  type={preference.preference_type != 1 ? 'radio' : 'checkbox'}
                  checked={isSelected}
                  onChange={() => onChangePreferenceIds(preference)}
                />
                {isMultiple && isSelected && <CheckMark />}
                {preference.name}
              </label>
            );
          })}
        </CollapsibleChips>
      </div>
    );
  };

  return (
    <div>
      {isApp && isTutorial && (
        <CountriesPrefsSelector countriesPrefs={countriesPrefs} currentPrefId={currentPrefId} />
      )}

      {sections.map((section) => {
        // 中身が1つだけのまとまりは、そのかたまりの見出しをセクションの見出しにする。
        // 複数入るまとまり（条件）は、セクションに名前を付けて中は小見出しにする
        const single = section.members.length === 1 ? section.members[0] : null;
        const label = single ? GROUP_LABELS[single.description] || {} : {};

        return (
          <section className="pr-2026__section" key={section.key}>
            <div className="pr-2026__card">
              <div className="pr-2026__group-head">
                <h2 className="pr-2026__group-title">
                  {section.title || label.title || (single && single.description)}
                </h2>
                {single && (
                  <span className="pr-2026__tag">
                    {single.preferenceType === 1 ? '複数選択可' : '1つ選択'}
                  </span>
                )}
              </div>
              {label.note && <p className="pr-2026__group-note">{label.note}</p>}

              {section.members.map((group) => renderGroupBody(group, !single))}
            </div>
          </section>
        );
      })}
    </div>
    );
  };
export default Edit;
